import { inspectionsKeys } from './api/inspections.gen';

// Shared reading of the SERVER-FILTERED assignee list (P05 §2, §8; W4; story
// 5-2a).
//
// This file exists for the same reason `lib/inspectionTypes.ts` does: two
// surfaces need the same answer and neither may import the other. The create
// WIZARD lives in `src/modules/scheduling`; the REASSIGN control lives on the
// shell's inspection detail screen — and a module is reached through the host,
// never by path (P18 §1, and the eslint rule that enforces it). So the query key
// and the copy live out here, where both can reach them.
//
// NOTHING HERE DECIDES ANYTHING. The list is computed by
// `backend/src/services/inspectionAssignment.service.js → listEligibleAssignees`
// from the same three inputs the assign guard uses (permission ∩ the type's
// perform roles ∩ the caller's team subtree). W4 is explicit that the filtering
// is the SERVER's — "the assignee dropdown never lists them (server-filtered
// endpoint, not client filter)" — so a client that narrowed the list itself
// would be a second copy of an authorization rule, and one that widened it would
// offer an assignment the API refuses.

/**
 * The picker's cache entry (DOD §4 G-19 — a NAMED factory entry, never an array
 * assembled at the call site).
 *
 * Hung under `inspectionsKeys.lists()` so one `invalidateQueries` after an
 * assignment refreshes the workload counts the rows carry.
 */
export const eligibleAssigneesKey = (filters: Record<string, unknown>): readonly unknown[] => [
  ...inspectionsKeys.lists(),
  'eligible-assignees',
  filters,
];

/**
 * What to say when the server returns nobody — one of P05 §8's "precise
 * empty/config/error states", and deliberately NOT "no results".
 *
 * The list being empty is an ANSWER, and there are only two shapes of it, which
 * need different actions from different people:
 *   * no type chosen yet — nothing is wrong, the question simply has no subject;
 *   * a type chosen and nobody eligible — either no active member holds a role
 *     the type permits, or everybody who does sits outside the caller's team.
 *     A manager can act on the second; only an owner can act on the first.
 */
export function noEligibleAssigneeCopy(hasType: boolean): { title: string; description: string } {
  if (!hasType) {
    return {
      title: 'Choose an inspection type first',
      description:
        'Who can carry out this inspection depends on which procedure it follows — the type decides which roles are allowed to perform it.',
    };
  }
  return {
    title: 'Nobody on your team can perform this inspection type',
    description:
      'Either no active member holds a role this type permits, or everyone who does sits outside the team you manage. Add the role to the inspection type, or ask an organisation owner to assign it.',
  };
}
